import React, { useCallback, useEffect, useState } from "react";
import "./PaymentSuccessful.css";
import { useNavigate } from "react-router-dom";
import CheckoutFooter from "../ComponentsCheckoutPage/Components/CheckoutFooter/CheckoutFooter";
import { CircularProgress } from "@mui/material";
import { NFOLogo, TickIcon } from "../../Assets/svgs";
import { formatDateTime } from "../../Utils/TimeConverter";
import {
  nfoFetchUserName,
  verifyNFOTransaction,
  verifyNFOTransactionRazorpay,
} from "../../Utils/CheckoutAPIs/CheckoutAPIs";
import { trackData } from "../../Utils/attribution/CheckoutAttribution";
import { isVisible } from "@testing-library/user-event/dist/utils";
import ReactGA from "react-ga4";
import { formatPhoneToE164 } from "../../Utils/helper";

// Add WebEngage type declaration
declare global {
  interface Window {
    webengage: {
      user: {
        login: (userId: string) => void;
        setAttribute: (attributeName: string, attributeValue: string | number | boolean | Date) => void;
      };
      track: (eventName: string, eventData: Record<string, any>) => void;
    };
  }
}

const PaymentSuccessful = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get("order_id");
  const schoolCode = urlParams.get("scl");

  const isInternationalTransaction = sessionStorage.getItem(
    "isInternationalTransaction"
  );
  let isInternational = false;
  try {
    isInternational = JSON.parse(isInternationalTransaction ?? "false");
  } catch (error) {
    console.error("Error parsing isInternationalTransaction:", error);
    isInternational = false;
  }
  const examDate = sessionStorage.getItem("examDate");
  const convertedExamDate = examDate && formatDateTime(examDate?.toString());
  const transactionAmount = sessionStorage.getItem("transactionAmount");

  const [alertToggle, setAlertToggle] = useState({
    alertMessage: "",
    isVisible: false,
  });
  const [studentUsername, setStudentUsername] = useState("");
  const [webengageLoginSent, setWebengageLoginSent] = useState(false);
  const [webengageEventSent, setWebengageEventSent] = useState(false);

  const REFER_MSG =
    "Referral link copied to clipboard. Share it with your friends";

  const [pageLoading, setLoading] = useState(true);
  useEffect(() => {
    // NOTE Arb tracking
    trackData("nfo_register_success", null, null, {
      success: true,
      payment_type: "prepaid",
      sale: {
        product_type: "nfo_register_success",
        amount_inr: transactionAmount ? Number(transactionAmount) : undefined,
        order_id: orderId ?? undefined,
      },
    });

    ReactGA.event({
      category: "Registration",
      action: "5678_registration_successful",
      label: "Payment_Success_Page",
    });
  }, []);

  // NOTE Load studentUsername from session storage on component mount
  useEffect(() => {
    const storedUsername = sessionStorage.getItem("studentUsername");
    if (storedUsername && !studentUsername) {
      setStudentUsername(storedUsername);
    }
  }, []);

  // NOTE WebEngage LOGIN effect - runs when studentUsername is set
  useEffect(() => {
    if (studentUsername && window.webengage && !webengageLoginSent) {
      window.webengage.user.login(studentUsername);
      setWebengageLoginSent(true);
    }
  }, [studentUsername, webengageLoginSent]);

  // NOTE WebEngage USER ATTRIBUTES - set user attributes after login
  useEffect(() => {
    if (studentUsername && window.webengage && webengageLoginSent && !webengageEventSent) {
      // Get user details from session storage
      const userName = sessionStorage.getItem("name") || "";
      const userEmail = sessionStorage.getItem("email") || "";
      const userPhone = sessionStorage.getItem("mobileNumber") || "";
      const userSchool = sessionStorage.getItem("school") || "";
      const userGrade = Number(sessionStorage.getItem("grade") || 0);
      const examDate = sessionStorage.getItem("examDate") || "";

      // Parse exam date and time
      const examScheduleTime = new Date(examDate);
      const examScheduleDate = new Date(examDate).toISOString().split('T')[0]; // YYYY-MM-DD format

      // Set user attributes with E.164 formatted phone number
      window.webengage.user.setAttribute("we_phone", formatPhoneToE164(userPhone));
      window.webengage.user.setAttribute("we_email", userEmail);
      window.webengage.user.setAttribute("name", userName);
      window.webengage.user.setAttribute("grade", userGrade);
      window.webengage.user.setAttribute("school_name", userSchool);
      window.webengage.user.setAttribute("exam_schedule_time", examScheduleTime);
      window.webengage.user.setAttribute("exam_schedule_date", examScheduleDate);

      console.log("🎯 WebEngage user attributes set:", {
        phone: formatPhoneToE164(userPhone),
        email: userEmail,
        name: userName,
        grade: userGrade,
        school_name: userSchool,
        exam_schedule_time: examScheduleTime,
        exam_schedule_date: examScheduleDate
      });

      console.log("🔍 Attribute format confirmation:", {
        "phone (type)": typeof userPhone,
        "email (type)": typeof userEmail,
        "name (type)": typeof userName,
        "grade (type)": typeof userGrade,
        "school_name (type)": typeof userSchool,
        "exam_schedule_time (type)": typeof examScheduleTime,
        "exam_schedule_time (constructor)": examScheduleTime.constructor.name,
        "exam_schedule_time (value)": examScheduleTime.toISOString(),
        "exam_schedule_date (type)": typeof examScheduleDate,
        "exam_schedule_date (constructor)": examScheduleDate.constructor.name,
        "exam_schedule_date (value)": examScheduleDate
      });
    }
  }, [studentUsername, webengageLoginSent, webengageEventSent]);

  // NOTE WebEngage nfo_reg_completed event - runs when studentUsername is set
  useEffect(() => {
    if (studentUsername && window.webengage && !webengageEventSent) {
      // Store studentUsername in session storage for persistence
      sessionStorage.setItem("studentUsername", studentUsername);
      
      // Get user details from session storage
      const userName = sessionStorage.getItem("name") || "";
      const userEmail = sessionStorage.getItem("email") || "";
      const userPhone = sessionStorage.getItem("mobileNumber") || "";
      const userSchool = sessionStorage.getItem("school") || "";
      const userGrade = Number(sessionStorage.getItem("grade") || 0);
      const userState = sessionStorage.getItem("state") || "";
      const userCity = sessionStorage.getItem("city") || "";
      const userPincode = Number(sessionStorage.getItem("pincode") || 0);
      const referredBy = sessionStorage.getItem("referredBy") || "";
      const examDate = sessionStorage.getItem("examDate") || "";
      const appliedCouponCode = sessionStorage.getItem("appliedCouponCode") || "";
      const couponDiscountAmount = Number(sessionStorage.getItem("couponDiscountAmount") || 0);
      const originalAmount = Number(sessionStorage.getItem("originalAmount") || 0);
      const transactionAmount = Number(sessionStorage.getItem("transactionAmount") || 0);
      const schoolCode = sessionStorage.getItem("schoolCode") || "";
      const individualReg = sessionStorage.getItem("individual_registration") === "true";

      // Calculate effective price and list price
      const effectivePrice = transactionAmount;
      const listPrice = originalAmount || transactionAmount;
      // MRP should be fetched from session storage (base_price from cart item)
      const mrp = Number(sessionStorage.getItem("mrp") || 699); // Get MRP from session storage
      const listDiscount = originalAmount ? (originalAmount - transactionAmount) : 0;

      // Parse exam date
       const examScheduleDate = examDate ? new Date(examDate) : new Date();
       const examScheduleTime = examDate ? new Date(examDate).toISOString() : new Date().toISOString();

       console.log("🎉 WebEngage nfo_reg_completed event triggered with data:", {
         // Strings
         "phone_number": formatPhoneToE164(userPhone),
         "school_name": userSchool,
         "exam_schedule_time": examScheduleTime,
         "referred_by": referredBy,
         "course_id": "NFO_2025_26", // FIXME what should this be?
         "email": userEmail,
         "state": userState,
         "student_name": userName,
         "transaction_id": orderId || "", // FIXME what should this be?
         "exam_mode": "Proctored Online (from your home)",
         "course_name": "National Finance Olympiad & Study Material",
         "coupon_code": appliedCouponCode,
         "city": userCity,
         "registration_id": orderId || "",// FIXME what should this be?
         "roll_number": studentUsername,

         // Date
         "exam_schedule_date": examScheduleDate,

         // Numbers
         "grade": userGrade,
         "coupon_discount_amount": couponDiscountAmount,
         "effective_price": effectivePrice,
         "list_price": listPrice,
         "mrp": mrp,
         "list_discount": listDiscount,
         "participated_year": 2025,
         "pincode": userPincode,

         // Booleans
         "is_school": !individualReg,
         "is_referred": Boolean(referredBy)
       });

       window.webengage.track("nfo_reg_completed", {
        // Strings
        "phone_number": formatPhoneToE164(userPhone),
        "school_name": userSchool,
        "exam_schedule_time": examScheduleTime,
        "referred_by": referredBy,
        "course_id": "NFO_2025_26",
        "email": userEmail,
        "state": userState,
        "student_name": userName,
        "transaction_id": orderId || "",
        "exam_mode": "online",
        "course_name": "National Finance Olympiad 2025-26",
        "coupon_code": appliedCouponCode,
        "city": userCity,
        "registration_id": orderId || "",
        "roll_number": studentUsername,

        // Date
        "exam_schedule_date": examScheduleDate,

        // Numbers
        "grade": userGrade,
        "coupon_discount_amount": couponDiscountAmount,
        "effective_price": effectivePrice,
        "list_price": listPrice,
        "mrp": mrp,
        "list_discount": listDiscount,
        "participated_year": 2025,
        "pincode": userPincode,

        // Booleans
        "is_school": !individualReg,
        "is_referred": Boolean(referredBy)
      });
      setWebengageEventSent(true);
    }
  }, [studentUsername, orderId, webengageEventSent]);

  const copyReferral = async () => {
    const referralLink = `Hi!\n\nI'm excited to share that I'm participating in India's largest financial literacy\ncompetition—National Finance Olympiad 2025-26! 🎉\n\nThis Olympiad is a unique opportunity for students like us to gain essential financial\nskills, from investing, financial planning to understanding banking and more. It's a\nchance to learn, grow, and prepare for a financially smart future!\n\nI'd love for you to join me in this journey! Compete with me and let's see\nwho can master the world of finance. 💸\n\nClick here to learn more and register now: https://nationalfinanceolympiad.com/nfo-mobile-5678`;
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setAlertToggle({ alertMessage: REFER_MSG, isVisible: true });
        setTimeout(() => {
          setAlertToggle({ alertMessage: "", isVisible: false });
        }, 10000);
      })
      .catch((err) => {
        console.error("Failed to copy the text to clipboard: ", err);
      });
    if (navigator.share) {
      await navigator
        .share({
          title: "National Finance Olympiad",
          text: "Hi!\n\nI'm excited to share that I'm participating in India's largest financial literacy\ncompetition—National Finance Olympiad 2025-26! 🎉\n\nThis Olympiad is a unique opportunity for students like us to gain essential financial\nskills, from investing, financial planning to understanding banking and more. It's a\nchance to learn, grow, and prepare for a financially smart future!\n\nI'd love for you to join me in this journey! Compete with me and let's see\nwho can master the world of finance. 💸\n\nClick here to learn more and register now: ",
          url: "https://nationalfinanceolympiad.com/nfo-mobile-5678",
        })
        .then(() => console.log("Successful share"))
        .catch((error) => console.log("Error sharing", error));
    }
  };

  const copyUserName = (userName: string) => {
    navigator.clipboard
      .writeText(userName)
      .then(() => {
        setAlertToggle({
          alertMessage: "Roll Number copied to clipboard.",
          isVisible: true,
        });
        setTimeout(() => {
          setAlertToggle({ alertMessage: "", isVisible: false });
        }, 10000);
      })
      .catch((err) => {
        console.error("Failed to copy the text to clipboard: ", err);
      });
  };

  const openNFOStudentHub = () => {
    window.open(
      `https://nfoportal.nationalfinanceolympiad.com/?&userName=${studentUsername}`,
      "_blank"
    );
  };

  const verifyPaymentStripe = useCallback(async (orderId: string) => {
    try {
      // Create a Checkout Session
      const response = await fetch(
        "https://streak-payments.el.r.appspot.com/nfo/stripe-metadata",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId: orderId,
          }),
        }
      );
      const data = await response.json();

      if (
        data.data.message ===
        "Metadata retrieved, student onboarded, and invoice sent successfully."
      ) {
        console.log("Payment Successful");
        setLoading(false);
      } else {
        navigate(`/CheckoutPage/?&scl=${schoolCode}`);
      }

      console.log(data.id);
      return data.clientSecret; // Assuming `data.id` is the client secret you need
    } catch (error) {
      navigate(`/CheckoutPage/?&scl=${schoolCode}`);
      // Handle the error accordingly
      return null; // Or throw error depending on your error handling strategy
    }
  }, []);

  const ir = sessionStorage.getItem("individual_registration");

  const verifyPaymentBilldesk = async (orderId: string) => {
    try {
      const errorStatuses = [
        "payment_processing_error",
        "user_cancelled_error",
        "acquirer_processing_error",
        "risk_validation_error",
      ];

      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransaction(orderId);
          console.log("This is new verify response after retry =", newResponse);
          //@ts-ignore
          const newPaymentStatus = newResponse.data.data.paymentStatus;
          if (newPaymentStatus === "success") {
            console.log("Payment Successful");
            return true;
          } else if (newPaymentStatus === "retry") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          } else if (errorStatuses.includes(newPaymentStatus)) {
            console.log("Payment failed");
            return false;
          } else {
            return false;
          }
        }
        return false;
      };

      const response = await verifyNFOTransaction(orderId);
      console.log("This is the verify response", response);
      //@ts-ignore
      const paymentStatus = response.data.data.paymentStatus;
      if (paymentStatus === "success") {
        console.log("Payment Successful");
        return true;
      } else if (paymentStatus === "retry") {
        console.log("Retry payment");
        return await retryPayment(3);
      } else if (errorStatuses.includes(paymentStatus)) {
        console.log("Payment failed");
        return false;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const verifyPayment = async (orderId: string) => {
    const paymentGateway = sessionStorage.getItem("pg");

    try {
      if (paymentGateway === "razorpay") {
        verifyNFOTransactionRazorpay(orderId)
          .then((response) => {
            if (response.status_code === 200) {
              if (
                response &&
                response.data &&
                response.data.status === "paid"
              ) {
                nfoFetchUserName(orderId).then((response) => {
                  if (
                    response.status_code === 200 ||
                    response.status === "Success"
                  ) {
                    setStudentUsername(response.data.studentUsername);
                  }
                });
              } else {
                navigate(`/CheckoutPage/?&scl=${schoolCode}`);
              }
              setLoading(false);
            } else {
              navigate(`/CheckoutPage/?&scl=${schoolCode}`);
            }
          })
          .catch((error) => {
            navigate(`/CheckoutPage/?&scl=${schoolCode}`);
          });
      } else {
        const response = await verifyPaymentBilldesk(orderId);
        if (response) {
          nfoFetchUserName(orderId)
            .then((response) => {
              if (
                response.status_code === 200 ||
                response.status === "Success"
              ) {
                setStudentUsername(response.data.studentUsername);
              }
            })
            .then(() => {
              setLoading(false);
            })
            .catch(() => {
              setLoading(false);
            });
        } else {
          navigate(`/CheckoutPage/?&scl=${schoolCode}`);
        }
      }
    } catch (err) {
      console.log(err);
      navigate(`/CheckoutPage/?&scl=${schoolCode}`);
    }
  };

  useEffect(() => {
    setLoading(true);

    if (orderId) {
      if (isInternational) {
        verifyPaymentStripe(orderId);
      } else {
        verifyPayment(orderId);
      }
    } else {
      navigate(`/CheckoutPage/?&scl=${schoolCode}`);
    }
  }, []);

  const navigate = useNavigate();
  return pageLoading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
      }}
    >
      <CircularProgress />
    </div>
  ) : (
    <div className="payment-successful-page">
      <img src={NFOLogo} className="nfo-logo" alt="NFO Logo" />
      <div className="line"></div>
      <div className="payment-success-container-parent">
        <div className="payment-success-container">
          <img src={TickIcon} className="tick-icon" alt="Success Tick" />
          <div className="successful-title">
            Thank you for registering for the National Finance Olympiad 2025-26.
          </div>
          <div className="successful-subtitle">
            {studentUsername !== ""
              ? ""
              : `We will be sharing your child's roll number and other details to
            your registered E-mail ID and mobile number shortly.`}
          </div>
        </div>
        <div className="payment-success-secondary-container">
          {ir === "true" && (
            <div className="payment-success-details-section">
              <div>
                <div className="date-of-exam">Date of Exam</div>
                <div className="date">
                  {convertedExamDate?.split(",")[0]},<br></br>
                  {convertedExamDate?.split(",")[1]} IST
                </div>
              </div>

              <div>
                <div className="total">Total</div>
                <div className="amount">
                  ₹{transactionAmount && transactionAmount.toString()}
                </div>
              </div>
            </div>
          )}

          <div className="successful-subtitle">
            {studentUsername !== "" && "Your NFO 2025-26 roll number is"}
          </div>

          {studentUsername !== "" && (
            <div
              onClick={() => copyUserName(studentUsername)}
              style={{
                marginTop: "20px",
                backgroundColor: "rgba(237, 247, 224, 1)",
                padding: "10px",
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                borderRadius: "8px",
                fontSize: "1.5rem",
                fontWeight: "900",
                letterSpacing: "0.1rem",
                color: "rgba(67, 137, 34, 1)",
                cursor: "pointer",
              }}
            >
              {studentUsername}
            </div>
          )}

          {studentUsername !== "" && (
            <div onClick={openNFOStudentHub} className="share-button">
              <div className="button-text">Access Student Hub</div>
            </div>
          )}

          <div
            onClick={copyReferral}
            style={{
              fontSize: "1rem",
              color: "rgba(36, 109, 194, 1)",
              fontFamily: "OggTextRegular",
              cursor: "pointer",
              marginTop: "20px",
              alignSelf: "center",
              textAlign: "center",
            }}
          >
            Share NFO with your friends & family
          </div>

          {alertToggle.isVisible && (
            <div
              className={`referral-toggle ${
                alertToggle.isVisible ? "fade-in" : "fade-out"
              } `}
            >
              &#9432; {alertToggle.alertMessage}
            </div>
          )}
        </div>
      </div>

      <CheckoutFooter />
    </div>
  );
};

export default PaymentSuccessful;
